import { PaymentMethodPendingVerificationType } from 'main/gql/globalTypes'
import { logError } from './common'

declare global {
  // tslint:disable-next-line
  interface Window {
    lego: any
    Plaid: any
  }
}

interface IUser {
  email: string
  name: string | null
  firstName: string | null
  lastName: string | null
}

enum VerificationStatus {
  PENDING_AUTOMATIC_VERIFICATION = 'pending_automatic_verification',
  PENDING_MANUAL_VERIFICATION = 'pending_manual_verification',
  MANUALLY_VERIFIED = 'manually_verified',
}

interface IConfig {
  token?: string | null
}

export interface IAccount {
  id: string
  name: string
  mask: string | null
  type: string
  subtype: string
  verification_status?: VerificationStatus | null
}

interface IInstitution {
  institution_id: string
  name: string
}

export interface IMetadata {
  link_session_id: string
  institution: IInstitution
  institution_id: string
  public_token: string
  account_id: string
  account: IAccount
  accounts: IAccount[]
}

export interface ISuccessData {
  publicToken: string
  stripeToken?: string
  metadata: IMetadata
}

enum ErrorCode {
  INSUFFICIENT_CREDENTIALS = 'INSUFFICIENT_CREDENTIALS',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  INVALID_MFA = 'INVALID_MFA',
  INVALID_FIELD = 'INVALID_FIELD',
  INVALID_SEND_METHOD = 'INVALID_SEND_METHOD',
  INVALID_UPDATED_USERNAME = 'INVALID_UPDATED_USERNAME',
  ITEM_LOCKED = 'ITEM_LOCKED',
  ITEM_LOGIN_REQUIRED = 'ITEM_LOGIN_REQUIRED',
  ITEM_NO_ERROR = 'ITEM_NO_ERROR',
  ITEM_NOT_SUPPORTED = 'ITEM_NOT_SUPPORTED',
  INCORRECT_DEPOSIT_AMOUNTS = 'INCORRECT_DEPOSIT_AMOUNTS',
  TOO_MANY_VERIFICATION_ATTEMPTS = 'TOO_MANY_VERIFICATION_ATTEMPTS',
  USER_SETUP_REQUIRED = 'USER_SETUP_REQUIRED',
  MFA_NOT_SUPPORTED = 'MFA_NOT_SUPPORTED',
  NO_ACCOUNTS = 'NO_ACCOUNTS',
  NO_AUTH_ACCOUNTS = 'NO_AUTH_ACCOUNTS',
  NO_INVESTMENT_ACCOUNTS = 'NO_INVESTMENT_ACCOUNTS',
  NO_LIABILITY_ACCOUNTS = 'NO_LIABILITY_ACCOUNTS',
  PRODUCT_NOT_READY = 'PRODUCT_NOT_READY',
  PRODUCTS_NOT_SUPPORTED = 'PRODUCTS_NOT_SUPPORTED',
}

export interface IErrorData {
  display_message: string
  error_code: ErrorCode
  error_message: string
  error_type: string
}

export function hasVoidedVerification(error: IErrorData) {
  return (
    error.error_code === ErrorCode.TOO_MANY_VERIFICATION_ATTEMPTS ||
    error.error_code === ErrorCode.INVALID_FIELD
  )
}

export function isPendingVerification(account: IAccount) {
  switch (account.verification_status) {
    case VerificationStatus.PENDING_AUTOMATIC_VERIFICATION:
    case VerificationStatus.PENDING_MANUAL_VERIFICATION:
      return true

    default:
      return false
  }
}

export function getVerificationType(account: IAccount) {
  switch (account.verification_status) {
    case VerificationStatus.PENDING_AUTOMATIC_VERIFICATION:
      return PaymentMethodPendingVerificationType.AUTOMATIC

    case VerificationStatus.PENDING_MANUAL_VERIFICATION:
    case VerificationStatus.MANUALLY_VERIFIED:
      return PaymentMethodPendingVerificationType.MANUAL

    default:
      throw new Error(`Unreachable case: ${account.verification_status}`)
  }
}

const ERROR = [
  'We are having problems contacting the bank account processor.',
  'Please try again, or contact us at hello@wonderschool.com.',
].join(' ')

// Open modal to connect a bank account using Plaid
// https://plaid.com/docs/quickstart/#creating-items-with-link-and-the-api
export function create(user: IUser, config?: IConfig): Promise<ISuccessData> {
  return new Promise((resolve, reject) => {
    if (!window.Plaid) {
      logError('Plaid not initialized')
      reject([ERROR])
      return
    }

    const additionalAuthParams = user.name
      ? {
          userEmailAddress: user.email,
          userLegalName: user.name,
          webhook: `${window.lego.config.DOMAIN}/api/hooks/plaid`,
        }
      : undefined

    window.Plaid.create({
      clientName: 'Wonderschool',
      env: window.lego.plaidEnv,
      key: window.lego.plaidPublicKey,
      onExit: (error: IErrorData | null, metadata: any) => {
        if (error) {
          logError('Error linking Plaid account', { error, metadata })
        }
        reject(error)
      },
      onSuccess: (publicToken: string, metadata: IMetadata) => {
        resolve({ publicToken, metadata })
      },
      product: 'auth',
      selectAccount: true,
      // Passing in additionalAuthParams enables all Auth paths, including
      // micro deposit verification. Important to note that Plaid requires
      // `userEmailAddress` and `userLegalName` for using all auth features,
      // hence if `user.name` is null, fall back to use Instant Auth only.
      // Reference: https://www.plaid.com/docs/#enabling-all-auth-features
      ...additionalAuthParams,
      // Plaid Link can be initialized with additional config like `token`
      // which is required for manual micro deposit verification.
      ...config,
    }).open()
  })
}
